import { useTranslation } from 'react-i18next';
import { Package, DollarSign, User, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import type { PurchaseRequest } from '../../../../api/queries/purchasing.queries';
import { StatusBadge, PriorityIndicator, getPriorityBorderClass, WorkflowTimeline } from '../shared';

interface PRCardProps {
  pr: PurchaseRequest;
  onClick: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onConvertToPO?: () => void;
  canApprove?: boolean;
  canCreatePO?: boolean;
}

export default function PRCard({
  pr,
  onClick,
  onApprove,
  onReject,
  onConvertToPO,
  canApprove = false,
  canCreatePO = false,
}: PRCardProps) {
  const { t, i18n } = useTranslation();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const priorityBorder = getPriorityBorderClass(pr.urgency);

  const workflowSteps = [
    { id: 'pr', label: 'PR', status: 'completed' as const },
    {
      id: 'approved',
      label: t('purchasing.approved'),
      status: pr.status === 'approved' || pr.status === 'converted' ? 'completed' as const : 'pending' as const,
    },
    {
      id: 'po',
      label: 'PO',
      status: pr.status === 'converted' ? 'completed' as const : 'pending' as const,
    },
    { id: 'grn', label: 'GRN', status: 'pending' as const },
  ];

  return (
    <div
      className={`rounded-xl border bg-card hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer ${priorityBorder}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{pr.prNumber}</h3>
              <StatusBadge status={pr.status} />
              <PriorityIndicator priority={pr.urgency} size="sm" />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {pr.branchId?.name || 'Unknown Branch'} • {formatDate(pr.createdAt)}
            </p>
          </div>

          {pr.hasFreeFormItems && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs">
              <AlertTriangle className="h-3 w-3" />
              {t('purchasing.freeFormItems')}
            </span>
          )}
        </div>

        {/* Quick Stats */}
        <div className="mt-4 flex items-center gap-5 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Package className="h-4 w-4" />
            {pr.items.length} {t('pos.items')}
          </span>
          <span className="flex items-center gap-1.5 font-medium">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            {formatCurrency(pr.totalEstimatedCost || 0)}
          </span>
          {pr.requestedBy && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <User className="h-4 w-4" />
              {pr.requestedBy.username}
            </span>
          )}
        </div>

        {/* Workflow Progress (only for approved PRs) */}
        {(pr.status === 'approved' || pr.status === 'converted') && (
          <div className="mt-4 pt-4 border-t">
            <WorkflowTimeline steps={workflowSteps} compact />
          </div>
        )}

        {/* Action Buttons */}
        {(pr.status === 'pending' && canApprove) || (pr.status === 'approved' && canCreatePO) ? (
          <div className="mt-4 pt-4 border-t flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {pr.status === 'pending' && canApprove && (
              <>
                <button
                  onClick={onApprove}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm font-medium hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                >
                  <CheckCircle className="h-4 w-4" />
                  {t('purchasing.approvePR')}
                </button>
                <button
                  onClick={onReject}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 text-sm font-medium hover:bg-rose-200 dark:hover:bg-rose-900/50 transition-colors"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </>
            )}

            {pr.status === 'approved' && canCreatePO && (
              <button
                onClick={onConvertToPO}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {t('purchasing.convertToPO')}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
